import { useMemo, useState } from "react";
import { Search, Heart, Clock } from "lucide-react";
import { getRecipeOptionsForMealSlot } from "../utils/mealPlanUtils";

/* ════════════════════════════════════════════════════════════════════════
   RECIPE SEARCH PICKER — searchable, slot-aware recipe selector.
   Used inside MealSlotEditModal AND the RecipeAddToSlotModal.
   ════════════════════════════════════════════════════════════════════════ */

export function RecipeSearchPicker({
  recipes = [],
  slotId,
  selectedRecipeId = null,
  onSelect,
  maxHeight = "360px",
  testIdPrefix = "recipe-search",
}) {
  const [q, setQ] = useState("");

  const { recommended, others } = useMemo(
    () => getRecipeOptionsForMealSlot(recipes || [], slotId),
    [recipes, slotId]
  );

  const filter = (list) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(r => {
      const haystack = [
        r.title || "",
        r.subtitle || "",
        r.category || "",
        (r.tags || []).join(" "),
        (r.ingredients || []).join(" "),
      ].join(" ").toLowerCase();
      return haystack.includes(needle);
    });
  };

  const filteredRecommended = filter(recommended);
  const filteredOthers = filter(others);
  const totalCount = filteredRecommended.length + filteredOthers.length;

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, category, tag or ingredient…"
          className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 pl-9 pr-3 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-rose-300"
          data-testid={`${testIdPrefix}-input`}
          autoFocus
        />
      </div>
      <div
        className="mt-2 rounded-2xl border border-gray-200 dark:border-slate-700 bg-gray-50/60 dark:bg-slate-800/40 overflow-y-auto"
        style={{ maxHeight }}
        data-testid={`${testIdPrefix}-list`}
      >
        {totalCount === 0 && (
          <p className="px-4 py-6 text-center text-xs font-bold text-gray-500">No recipes match — try a different search.</p>
        )}
        {filteredRecommended.length > 0 && (
          <SectionLabel>{slotId ? `Recommended for ${slotId}` : "Recipes"}</SectionLabel>
        )}
        {filteredRecommended.map(r => (
          <RecipeRow
            key={r.id}
            recipe={r}
            selected={selectedRecipeId === r.id}
            onSelect={() => onSelect?.(r)}
            testId={`${testIdPrefix}-row-${r.id}`}
          />
        ))}
        {filteredOthers.length > 0 && filteredRecommended.length > 0 && (
          <SectionLabel>Other recipes</SectionLabel>
        )}
        {filteredOthers.map(r => (
          <RecipeRow
            key={r.id}
            recipe={r}
            selected={selectedRecipeId === r.id}
            onSelect={() => onSelect?.(r)}
            testId={`${testIdPrefix}-row-${r.id}`}
          />
        ))}
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <p className="sticky top-0 z-[1] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-wider text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] bg-transparent dark:bg-transparent backdrop-blur border-b border-[color:var(--ara-line-soft)] dark:border-rose-900/40">
      {children}
    </p>
  );
}

function RecipeRow({ recipe, selected, onSelect, testId }) {
  const totalMin = (recipe.prepTime || 0) + (recipe.cookTime || 0);
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-b border-gray-100 dark:border-slate-800 hover:bg-white dark:hover:bg-slate-800 transition ${selected ? "bg-transparent dark:bg-transparent" : ""}`}
      data-testid={testId}
      aria-pressed={selected}
    >
      <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center     dark: dark: dark: flex-shrink-0">
        {recipe.imageUrl ? (
          <img src={recipe.imageUrl} alt={recipe.title} className="w-full h-full object-cover" />
        ) : (
          <span className="text-xl" aria-hidden>{recipe.imageEmoji || "🍽️"}</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <p className="text-sm font-extrabold text-gray-900 dark:text-gray-50 truncate">{recipe.title}</p>
          {recipe.favourite && <Heart className="w-3 h-3 text-[color:var(--ara-ink-muted)] flex-shrink-0" strokeWidth={1.5} fill="currentColor" />}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] font-bold text-gray-500">
          <span className="px-1.5 py-0.5 rounded-full bg-transparent dark:bg-transparent text-[color:var(--ara-ink-muted)] dark:text-[color:var(--ara-ink-muted)] font-extrabold uppercase tracking-wider">{recipe.category || "Recipe"}</span>
          {totalMin > 0 && (
            <span className="inline-flex items-center gap-0.5">
              <Clock className="w-2.5 h-2.5" strokeWidth={3} />{totalMin}m
            </span>
          )}
          {recipe.caloriesPerServe ? <span>· {Math.round(recipe.caloriesPerServe)} kcal</span> : null}
          {(recipe.tags || []).slice(0, 2).map(t => (
            <span key={t} className="px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300">{t}</span>
          ))}
        </div>
      </div>
    </button>
  );
}

export default RecipeSearchPicker;
